import View                   from '@widesk-ui/components/View';
import React                  from 'react';
import { message }            from '@widesk-ui/hooks/useMessage';
import uploadFileForModelUrn  from '@/helpers/uploadFileForModelUrn';
import { isBlockedByLimit }   from '@/components/Functionality/functionalityGuards';
import { functionalityStore } from '@/components/Functionality';
import currentEditorStore     from '@/TokamapEditor/stores/currentEditorStore';
import ImageThumbnail         from '@/TokamapEditor/ux/AreaSidebar/ImageThumbnail';
import InputOk                from '@/TokamapEditor/ux/AreaSidebar/InputOk';
import validateImageUrl       from '@/TokamapEditor/helpers/validateImageUrl';
import ImportedImageBox       from '@/TokamapEditor/ux/ImportedImageBox';
import { isStorageUrl }       from '@/helpers/storageImageCrossOrigin';
import pickMapImage           from '@/TokamapEditor/helpers/pickMapImage';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

/**
 * Champ dont la valeur est l'URL d'une image : collée telle quelle, choisie parmi celles du projet,
 * ou obtenue en déposant un fichier. Envoyé comme une image de la carte ouverte (`map_image`), dont les fichiers sont publics
 * — l'URL doit s'ouvrir depuis la carte publiée — et compté dans le quota `nb_images`.
 */
export default function ImageUrlInput(props: {
	value?: string;
	onChange?: (value?: string) => void;
}) {
	const [dragging, setDragging] = React.useState(false);
	const [uploading, setUploading] = React.useState(false);

	const upload = async (picked: File) => {
		if (!ACCEPTED_IMAGE_TYPES.includes(picked.type)) {
			return message.error('Seules les images JPG et PNG sont acceptées.');
		}

		const mapUrn = currentEditorStore.map?.urn;

		if (!mapUrn) return message.error('Aucune carte ouverte : l\'image ne peut pas être envoyée.');
		if (await isBlockedByLimit({ reference: 'nb_images' })) return;

		setUploading(true);

		try {
			const uploaded = await uploadFileForModelUrn(mapUrn, 'map_image', picked);

			functionalityStore.refreshUsagesOf('nb_images');
			props.onChange?.(uploaded.downloadPath);
		} catch (err) {
			message.exception(err);
		} finally {
			setUploading(false);
		}
	};

	const pickProjectImage = (title: string) => pickMapImage(title, src => props.onChange?.(src));

	return (
		<View row gap="xs">
			{/* `stopPropagation` : l'éditeur écoute lui aussi les dépôts, pour importer une image sur la carte. */}
			<div
				style={{ display: 'flex', flexShrink: 0 }}
				onDragOver={e => {
					e.preventDefault();
					e.stopPropagation();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={e => {
					e.preventDefault();
					e.stopPropagation();
					setDragging(false);

					const dropped = e.dataTransfer.files?.[0];

					if (dropped) upload(dropped);
				}}
			>
				<ImageThumbnail
					fit="cover"
					src={props.value}
					loading={uploading}
					highlighted={dragging}
					onPick={() => pickProjectImage(props.value ? 'Remplacer l\'image' : 'Ajouter une image')}
				/>
			</div>

			<View flex>
				{isStorageUrl(props.value) ? (
					<ImportedImageBox
						onReplace={() => pickProjectImage('Remplacer l\'image')}
						onRemove={() => props.onChange?.(undefined)}
					/>
				) : (
					<InputOk
						value={props.value}
						placeholder="Coller l'adresse d'une image"
						debounce={600}
						onSubmit={async value => {
							const trimmed = (value || '').trim();

							validateImageUrl(trimmed);

							props.onChange?.(trimmed || undefined);
						}}
					/>
				)}
			</View>
		</View>
	);
}
